import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { LATIN_REGEXP } from "../../upload/upload.component";
import { PwdRulesState } from "./PwdRulesState";
import zxcvbn, { ZXCVBNResult } from "zxcvbn";

export class PwdFormService {
  form = new FormGroup<{
    password: FormControl<string>;
    confirmPassword: FormControl<string>;
    agreement: FormControl<boolean>;
    mainPassword: FormControl<string>;
  }>({
    password: new FormControl<string>('', {
      validators: [Validators.required, Validators.maxLength(256),
        this.createPwdLengthValidator(),
        this.createPwdLatinValidator(),
        this.createPwdAtLeastOneDigit(),
        this.createPwdAtLeastOneSpecSym(),
        this.createPwdUpperLowerCaseValidator(),
        this.createPwdStrengthValidator()],
    }),
    confirmPassword: new FormControl<string>('', {
      validators: [Validators.required, Validators.maxLength(256)],
    }),
    agreement: new FormControl<boolean>(false),
    // used only in reset password from account settings
    mainPassword: new FormControl<string>(''),
  }, { validators: [this.createPwdsMatchValidator()] });

  pwdRulesState = new PwdRulesState();

  constructor() {
    this.form.statusChanges
      .subscribe(() => {
        this.updateRuleStyles();
      });
  }

  createPwdsMatchValidator(): ValidatorFn {
    return (form: FormGroup): ValidationErrors => {
      const password = form.controls.password.getRawValue();
      const confirmPassword = form.controls.confirmPassword.getRawValue();

      return (password && confirmPassword && (password === confirmPassword))
        ? null
        : { pwdsMismatchError: true };
    };
  }

  createPwdLengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const password = control.getRawValue();

      const pwdLen12 = len12RegEx.test(password);
      return pwdLen12 ? null : { pwdLenError: true };
    };
  }

  createPwdUpperLowerCaseValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const password = control.getRawValue();

      const hasUpperCase = upperCaseRegEx.test(password);
      const hasLowerCase = lowerCaseRegEx.test(password);

      return hasUpperCase && hasLowerCase ? null : { pwdUpperLowerCaseError: true };
    };
  }

  createPwdAtLeastOneDigit(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const password = control.getRawValue();

      const pwdAtLeastOneDigit = atLeastOneDigit.test(password);
      return pwdAtLeastOneDigit ? null : { pwdAtLeastOneDigitError: true };
    };
  }

  createPwdAtLeastOneSpecSym(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const password = control.getRawValue();

      const pwdAtLeastSpecSym = atLeastOneSpecSym.test(password);
      return pwdAtLeastSpecSym ? null : { pwdAtLeastOneSpecSymError: true };
    };
  }

  createPwdLatinValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const password = control.getRawValue();

      const pwdLatinLetter = latinLetterRegEx.test(password);
      const pwsLatinWithSpecSym = LATIN_REGEXP.test(password);
      return (password && pwdLatinLetter && pwsLatinWithSpecSym) ? null : { pwdLatinError: true };
    };
  }

  createPwdStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      const password = control.getRawValue();
      let passwordStrength: number;

      if (!password) {
        // only use strength 0 when no password provided
        passwordStrength = 0;
      } else {
        const result: ZXCVBNResult = zxcvbn(password);

        // 0 and 1 here are considered same and displayed as single red bar
        passwordStrength = result.score === 0 ? 1 : result.score;
      }

      return passwordStrength >= passwordStrengthLowerThershold ? null : { pwdStrengthError: passwordStrength };
    };
  }


  private updateRuleStyles(): void {
    const passwordControl = this.form.controls.password;

    this.pwdRulesState.pwdLengthRule.setStyles(!passwordControl.hasError('pwdLenError'));
    this.pwdRulesState.pwdUpperLowerCaseRule.setStyles(!passwordControl.hasError('pwdUpperLowerCaseError'));
    this.pwdRulesState.pwdAtLeastOneDigit.setStyles(!passwordControl.hasError('pwdAtLeastOneDigitError'));
    this.pwdRulesState.pwdAtLeastOneSpecSym.setStyles(!passwordControl.hasError('pwdAtLeastOneSpecSymError'));
    this.pwdRulesState.pwdLatinRule.setStyles(!passwordControl.hasError('pwdLatinError'));

    // pwdStrengthError = 0 isn't considered an error with hasError
    this.pwdRulesState.pwdStrengthRule.setStyles(
      passwordControl.errors?.pwdStrengthError === undefined || passwordControl.errors?.pwdStrengthError === null);

    const form = this.form;
    const passwordsMatch = !form.hasError('pwdsMismatchError');
    this.pwdRulesState.pwdMatchRule.setStyles(passwordsMatch);
  }
}

const passwordStrengthLowerThershold = 4;

const len12RegEx = /^.{12,}$/;
const lowerCaseRegEx = /^(?=.*[a-z]).+$/u;
const upperCaseRegEx = /^(?=.*[A-Z]).+$/u;
const atLeastOneDigit = /\d/;
const atLeastOneSpecSym = /[!"#$%&'()* +,\-./:;<=>?@[\\\]^_`{|}~]/;
const latinLetterRegEx = /^(?=.*[A-Za-z]).*$/;
